import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import ActivityTimeline from "@/components/activities/ActivityTimeline";
import { CircleNotch } from "@phosphor-icons/react";

async function fetchActivitiesInto(setRows, params) {
  try {
    const { data } = await api.get("/activities", { params });
    setRows(data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

async function fetchActivityRefsInto(setTypes, setUsers) {
  try {
    const [types, users] = await Promise.all([api.get("/reference/activity-types"), api.get("/users/options")]);
    setTypes(types.data);
    setUsers(users.data);
  } catch (e) {
    toast.error("Filter options could not be loaded. Please refresh.");
  }
}

const ANY = "__any__";
const FU_ORDER = { overdue: 0, due_today: 1, upcoming: 2 };

export default function Activities() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Dashboard "No outcome recorded" drill-through (item 5): show only records
  // that still need a manual outcome correction.
  const missingOutcome = searchParams.get("missing_outcome") === "1";
  const [view, setView] = useState("all"); // all | followups
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [typeId, setTypeId] = useState(ANY);
  const [ownerId, setOwnerId] = useState(ANY);
  const [fuStatus, setFuStatus] = useState(ANY);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const params = useMemo(
    () => ({
      activity_type_id: typeId === "__any__" ? undefined : typeId,
      owner_id: ownerId === "__any__" ? undefined : ownerId,
      fu_status: fuStatus === "__any__" ? undefined : fuStatus,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [typeId, ownerId, fuStatus, dateFrom, dateTo]
  );

  const helpers = useRef({ fetchActivitiesInto, fetchActivityRefsInto });
  const load = useCallback(() => helpers.current.fetchActivitiesInto(setRows, params), [helpers, setRows, params]);
  useEffect(() => {
    load();
  }, [load]);
  const loadRefs = useCallback(() => helpers.current.fetchActivityRefsInto(setTypes, setUsers), [helpers, setTypes, setUsers]);
  useEffect(() => {
    loadRefs();
  }, [loadRefs]);

  const followUps = (rows || [])
    .filter((a) => ["overdue", "due_today", "upcoming"].includes(a.follow_up_status))
    .sort((a, b) =>
      FU_ORDER[a.follow_up_status] - FU_ORDER[b.follow_up_status] ||
      `${a.follow_up_date} ${a.follow_up_time || ""}`.localeCompare(`${b.follow_up_date} ${b.follow_up_time || ""}`));

  const allRows = missingOutcome
    ? (rows || []).filter((a) => !(a.outcome || "").trim() || a.outcome === "No outcome recorded")
    : rows;

  return (
    <div className="p-6 sm:p-8" data-testid="activities-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Sales</p>
      <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#032852] sm:text-4xl">Activities</h1>
      <p className="mt-2 text-sm text-[#6D6D70]">Every interaction on the record — and no follow-up left behind.</p>

      <div className="mt-5 flex gap-2">
        <button onClick={() => setView("all")} data-testid="view-all"
          className={`rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wide ${view === "all" ? "bg-[#032852] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}>
          All activities
        </button>
        <button onClick={() => setView("followups")} data-testid="view-followups"
          className={`rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wide ${view === "followups" ? "bg-[#032852] text-white" : "bg-[#F4F4F5] text-[#52525B] hover:bg-[#E4E4E7]"}`}>
          Follow-ups
        </button>
      </div>

      {view === "all" && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="From date" className="sm:w-40" testId="filter-date-from" />
          <DatePicker value={dateTo} onChange={setDateTo} placeholder="To date" className="sm:w-40" testId="filter-date-to" />
          <Select value={typeId} onValueChange={setTypeId}>
            <SelectTrigger className="rounded-sm sm:w-44" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All types</SelectItem>
              {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger className="rounded-sm sm:w-44" data-testid="filter-owner"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>All owners</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={fuStatus} onValueChange={setFuStatus}>
            <SelectTrigger className="rounded-sm sm:w-44" data-testid="filter-fu-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any follow-up status</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="due_today">Due today</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="none">No follow-up</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {view === "all" && missingOutcome && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#F59E0B]/40 bg-[#FFFBEB] px-4 py-2.5"
          data-testid="missing-outcome-banner">
          <p className="text-xs font-semibold text-[#B45309]">
            Showing activities without a recorded outcome — open the prospect record to correct each one.
          </p>
          <button type="button" onClick={() => setSearchParams({})}
            className="text-xs font-bold text-[#B45309] underline" data-testid="clear-missing-outcome">
            Show all activities
          </button>
        </div>
      )}

      <div className="mt-4">
        {rows === null ? (
          <div className="flex justify-center py-16"><CircleNotch size={24} className="animate-spin text-[#032852]" /></div>
        ) : view === "all" ? (
          <ActivityTimeline activities={allRows} showProspect onDone={load}
            onEdit={(a) => navigate(`/prospects/${a.prospect_id}`)}
            onCompleteAndLog={(a) => navigate(`/prospects/${a.prospect_id}`)} />
        ) : followUps.length === 0 ? (
          <p className="rounded-sm border border-dashed border-[#E4E4E7] bg-white py-10 text-center text-sm text-[#6D6D70]" data-testid="no-followups">
            Nothing needs attention — no overdue, due-today or upcoming follow-ups.
          </p>
        ) : (
          <div data-testid="followup-list"><ActivityTimeline activities={followUps} showProspect onDone={load}
            onEdit={(a) => navigate(`/prospects/${a.prospect_id}`)}
            onCompleteAndLog={(a) => navigate(`/prospects/${a.prospect_id}`)} /></div>
        )}
      </div>
    </div>
  );
}
